import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { auth, buildingTemplates, getAccessToken, setAccessToken, signOutAndWait } from "../api";
import { endActiveVoiceSessions } from "../voice/activeSession";

interface AuthState {
  isAuthenticated: boolean;
  loading: boolean;
  /** User ID from JWT sub claim. Undefined if not present in token. */
  userId?: string;
  /** User role from JWT (e.g. "parent", "child"). Undefined if not present in token. */
  role?: string;
  /** Tenant ID from JWT. Undefined if not present in token. */
  tenantId?: string;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => void;
  /** Re-check auth state (e.g. after SignupFlow sets the token directly). */
  refreshSession: () => Promise<void>;
  /** The boot refresh could not reach the server; the stored session is kept
   *  and retried, and `loading` stays true meanwhile (ipad-native-shell 2.8). */
  offline?: boolean;
}

/** How often a boot refresh that found the server unreachable tries again, on
 *  top of the browser's `online` event (which does not fire when the device is
 *  online but the API is not). */
export const OFFLINE_RETRY_MS = 15_000;

/**
 * The signed-in account's id from a login response's access token. /auth/login
 * returns no id, and the username is not one: passing it on as the child id made
 * every avatar call answer 400 "Invalid child_id" until a reload. "" when there is
 * no readable token; App then takes the id from the session's JWT.
 */
export function accountIdFromLogin(data: { access_token?: string | null }): string {
  return (data.access_token && extractJwtClaims(data.access_token).userId) || "";
}

export function extractJwtClaims(token: string): { userId?: string; role?: string; tenantId?: string } {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return {};
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(base64)) as Record<string, unknown>;
    return {
      userId: typeof payload.sub === "string" ? payload.sub : undefined,
      role: typeof payload.role === "string" ? payload.role : undefined,
      tenantId: typeof payload.tenant_id === "string" ? payload.tenant_id : undefined,
    };
  } catch {
    return {};
  }
}

function childSessionMarked(): boolean {
  try { return sessionStorage.getItem("is_child_token") === "1"; } catch { return false; }
}

// Exported so consumers that need to fall back gracefully (rather than
// throw when no provider is mounted — e.g. visitor-shell test fixtures or
// future preview surfaces) can read the context directly. The public
// useAuth() hook still throws on missing provider; for opt-in tolerant
// reads, use useContext(AuthContext) and handle null.
export const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | undefined>();
  const [role, setRole] = useState<string | undefined>();
  const [tenantId, setTenantId] = useState<string | undefined>();
  const [offline, setOffline] = useState(false);

  function applyToken(token: string) {
    setAccessToken(token);
    const claims = extractJwtClaims(token);
    setUserId(claims.userId);
    // A one-tap child session's token carries no role claim. setAccessToken keeps the child
    // markers only when the token applied is that session's own, so they name it a child.
    setRole(claims.role ?? (childSessionMarked() ? "child" : undefined));
    setTenantId(claims.tenantId);
  }

  useEffect(() => {
    // If user explicitly logged out, don't auto-refresh back into the old session
    if (typeof sessionStorage !== "undefined" && sessionStorage.getItem("logged_out")) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const retry = () => {
      if (timer) { clearTimeout(timer); timer = null; }
      window.removeEventListener("online", retry);
      void attempt();
    };
    const attempt = () => auth.refresh().then((r) => {
      if (cancelled) return;
      applyToken(r.access_token);
      setIsAuthenticated(true);
      setOffline(false);
      setLoading(false);
      // A restored session is headed for the World: start the town's template list
      // now rather than after Phaser boots (public, ~50 kB; PhaserBoot starts the
      // images). Best effort, and after the sign-in, which it must never undo.
      try { void buildingTemplates.prefetchPublic().catch(() => {}); } catch { /* registry fetches it */ }
    }).catch((err: unknown) => {
      if (cancelled) return;
      if ((err as { code?: string } | null)?.code === "offline") {
        // The server never answered — a good stored session is not a sign-out.
        // Stay on the boot ground and try again when the network is back.
        setOffline(true);
        window.addEventListener("online", retry);
        timer = setTimeout(retry, OFFLINE_RETRY_MS);
        return;
      }
      setOffline(false);
      setLoading(false);
    });
    void attempt();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      window.removeEventListener("online", retry);
    };
  }, []);

  /**
   * Listen for `auth:invalidated` from api.ts — fired when the token-refresh
   * helper detects the watermark 401 ("Token invalidated by password change").
   * Without this, a password reset on device A leaves device B in a stuck
   * loading state with no idea why every request is failing.
   *
   * On detection: clear local state, mark a banner reason in sessionStorage
   * for the SignIn screen to read, and redirect to the login page.
   */
  useEffect(() => {
    function handleInvalidated() {
      try {
        sessionStorage.setItem("auth_invalidated_reason", "password_changed_elsewhere");
        sessionStorage.setItem("logged_out", "1");
      } catch {
        /* SSR/test */
      }
      setAccessToken("");
      try {
        localStorage.removeItem("access_token");
        sessionStorage.removeItem("child_session_token");
        sessionStorage.removeItem("is_child_token");
      } catch {
        /* ignore */
      }
      setIsAuthenticated(false);
      setUserId(undefined);
      setRole(undefined);
      setTenantId(undefined);
      // Force navigation. Using assign (not replace) so the user can navigate
      // back if they want, but the session is already torn down.
      if (typeof window !== "undefined") {
        window.location.href = "/?view=login";
      }
    }
    window.addEventListener("auth:invalidated", handleInvalidated);
    return () => window.removeEventListener("auth:invalidated", handleInvalidated);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const r = await auth.login(email, password);
    try { sessionStorage.removeItem("logged_out"); } catch { /* SSR/test */ }
    applyToken(r.access_token);
    setIsAuthenticated(true);
  }, []);

  const register = useCallback(async (email: string, password: string) => {
    const r = await auth.register(email, password);
    try { sessionStorage.removeItem("logged_out"); } catch { /* SSR/test */ }
    applyToken(r.access_token);
    setIsAuthenticated(true);
  }, []);

  const signOut = useCallback(async () => {
    setAccessToken("");
    localStorage.removeItem("access_token");
    setIsAuthenticated(false);
    setUserId(undefined);
    setRole(undefined);
    setTenantId(undefined);
    // Clear the refresh token cookie on the server (signOutRequest retries once
    // on failure; on native it also revokes the refresh grant and clears the
    // Keychain copy). Awaited, bounded: navigating first let WebKit cancel the
    // POST, and the grown-up's session outlived Log out on a shared iPad.
    // NOTE: this does NOT clear the wbm_device trust cookie — trust must survive
    // logout so the profile picker can offer one-tap sign-in afterward.
    await signOutAndWait();
    // Route by device trust on the way out: App sends a trusted device to the
    // profile picker and otherwise falls back to sign-in (it reads the
    // `logged_out` flag to prefer sign-in over the landing page). Going straight
    // to "/?view=login" here would bypass the picker on trusted devices.
    window.location.href = "/";
  }, []);

  const logout = useCallback(() => {
    // Mark logout intent immediately so a page refresh won't auto-restore the session
    try { sessionStorage.setItem("logged_out", "1"); } catch { /* SSR/test */ }
    // A live voice session ends first, while the token can still authorize its
    // end call — clearing it below left the room running (voice/activeSession.ts).
    // Capped, so sign-out never waits long on the network.
    const ending = endActiveVoiceSessions();
    if (ending) {
      void ending.then(signOut);
      return;
    }
    void signOut();
  }, [signOut]);

  const refreshSession = useCallback(async () => {
    try {
      const r = await auth.refresh();
      try { sessionStorage.removeItem("logged_out"); } catch { /* SSR/test */ }
      applyToken(r.access_token);
      setIsAuthenticated(true);
    } catch {
      // If refresh fails, check if there's already a token set in memory
      const token = getAccessToken();
      if (token) {
        applyToken(token);
        setIsAuthenticated(true);
      }
    }
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, loading, userId, role, tenantId, login, register, logout, refreshSession, offline }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
